const thumbnailCache = new Map<string, { small: string; large: string }>();

export async function getVideoThumbnails(videoSrc: string) {
    const cached = thumbnailCache.get(videoSrc);
    if (cached) return cached;

    return generateVideoThumbnails(videoSrc);
}

async function generateVideoThumbnails(videoSrc: string) {
    const smallDimensions = { width: 320, height: 240 };
    const largeDimensions = { width: 1280, height: 720 };

    const [smallThumbnail, largeThumbnail] = await Promise.all([
        generateClientSideThumbnail(videoSrc, smallDimensions.width, smallDimensions.height),
        generateClientSideThumbnail(videoSrc, largeDimensions.width, largeDimensions.height),
    ]);

    const result = {
        small: smallThumbnail,
        large: largeThumbnail,
    };

    thumbnailCache.set(videoSrc, result);

    return result;
}

function generateClientSideThumbnail(
    videoSrc: string,
    width: number,
    height: number,
): Promise<string> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');

        video.crossOrigin = 'anonymous';
        video.src = videoSrc;

        const cleanup = () => {
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('error', handleError);
            video.removeEventListener('seeked', handleSeeked);
            video.src = '';
            video.load();
        };

        const handleError = (error: Event) => {
            cleanup();
            reject(new Error(`Failed to load video: ${error}`));
        };

        const handleSeeked = () => {
            try {
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnail = canvas.toDataURL('image/jpeg', 0.7);

                cleanup();
                resolve(thumbnail);
            } catch (error) {
                cleanup();
                reject(new Error(`Failed to generate thumbnail: ${error}`));
            }
        };

        const handleLoadedData = () => {
            video.currentTime = 0.1;
        };

        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('error', handleError);
    });
}
